/**
 * Lightweight SVG chart renderer for the chat widget.
 * Supports bar, line, pie, and area charts from a simple JSON spec.
 * Zero external dependencies - generates SVG markup directly.
 * Legend is rendered as HTML (not SVG) for proper RTL/bidi support.
 */

export interface ChartDataset {
  label?: string;
  values: number[];
  color?: string;
}

export interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'area';
  title?: string;
  data: {
    labels: string[];
    datasets: ChartDataset[];
  };
}

const PALETTE = [
  '#4f46e5', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#7c3aed', '#db2777', '#2563eb',
];

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Build an HTML legend div from datasets (rendered outside SVG for proper RTL). */
function buildLegendHtml(datasets: ChartDataset[]): string {
  const labeled = datasets.filter(ds => ds.label);
  if (labeled.length === 0) return '';
  const items = labeled.map(ds => {
    const label = escapeXml(ds.label!);
    return `<span class="chart-legend-item"><span class="chart-legend-dot" style="background:${ds.color}"></span>${label}</span>`;
  }).join('');
  return `<div class="chart-legend">${items}</div>`;
}

/** Build an HTML legend for pie charts with percentages. */
function buildPieLegendHtml(labels: string[], values: number[], total: number): string {
  if (labels.length === 0) return '';
  const items = labels.map((label, i) => {
    const pct = total > 0 ? ((Math.max(0, values[i]) / total) * 100).toFixed(0) : '0';
    const color = PALETTE[i % PALETTE.length];
    const displayLabel = escapeXml(label.length > 20 ? label.slice(0, 19) + '…' : label);
    return `<span class="chart-legend-item"><span class="chart-legend-dot" style="background:${color}"></span>${displayLabel} (${pct}%)</span>`;
  }).join('');
  return `<div class="chart-legend chart-legend-wrap">${items}</div>`;
}

/** Render a chart into a container element from its data-chart-spec attribute. */
export function renderChart(container: HTMLElement): void {
  const raw = container.getAttribute('data-chart-spec');
  if (!raw) return;

  let spec: ChartSpec;
  try {
    spec = JSON.parse(raw);
  } catch {
    container.textContent = 'Invalid chart data';
    container.classList.add('chart-error');
    return;
  }

  if (!spec.type || !spec.data?.labels || !spec.data?.datasets?.length) {
    container.textContent = 'Invalid chart specification';
    container.classList.add('chart-error');
    return;
  }

  // Assign default colors
  spec.data.datasets.forEach((ds, i) => {
    if (!ds.color) ds.color = PALETTE[i % PALETTE.length];
  });

  let svg: string;
  let legendHtml: string;
  switch (spec.type) {
    case 'bar':  svg = renderBar(spec); legendHtml = buildLegendHtml(spec.data.datasets); break;
    case 'line': svg = renderLine(spec, false); legendHtml = buildLegendHtml(spec.data.datasets); break;
    case 'area': svg = renderLine(spec, true); legendHtml = buildLegendHtml(spec.data.datasets); break;
    case 'pie':  svg = renderPie(spec); legendHtml = buildPieLegendHtml(
      spec.data.labels, spec.data.datasets[0].values,
      spec.data.datasets[0].values.reduce((s, v) => s + Math.max(0, v), 0)
    ); break;
    default:
      container.textContent = `Unsupported chart type: ${spec.type}`;
      container.classList.add('chart-error');
      return;
  }

  // Preserve the expand button (inserted by markdown renderer) before replacing content
  const expandBtn = container.querySelector('.expand-icon');
  container.innerHTML = svg + legendHtml;
  if (expandBtn) container.appendChild(expandBtn);
  container.classList.add('chart-rendered');
}

// --- Layout constants ---
const W = 460;
const PAD = { top: 32, right: 16, left: 65 };

function titleSvg(title: string | undefined, width: number): string {
  if (!title) return '';
  return `<text class="chart-title" x="${width / 2}" y="18" text-anchor="middle">${escapeXml(title)}</text>`;
}

function computeNiceTicks(min: number, max: number, count: number): number[] {
  if (max === min) { max = min + 1; }
  const range = max - min;
  const rough = range / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const residual = rough / mag;
  let nice: number;
  if (residual <= 1.5) nice = 1 * mag;
  else if (residual <= 3) nice = 2 * mag;
  else if (residual <= 7) nice = 5 * mag;
  else nice = 10 * mag;

  const start = Math.floor(min / nice) * nice;
  const ticks: number[] = [];
  for (let v = start; v <= max + nice * 0.01; v += nice) {
    ticks.push(Math.round(v * 1e10) / 1e10);
  }
  return ticks;
}

function formatNum(n: number): string {
  if (Math.abs(n) >= 1e6) {
    const v = n / 1e6;
    return (v % 1 === 0 ? String(v) : v.toFixed(1)) + 'M';
  }
  if (Math.abs(n) >= 1e3) {
    const v = n / 1e3;
    return (v % 1 === 0 ? String(v) : v.toFixed(1)) + 'K';
  }
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

function truncLabel(label: string, maxLen: number): string {
  if (label.length <= maxLen) return label;
  return label.slice(0, maxLen - 1) + '…';
}

// --- Bar Chart ---
function renderBar(spec: ChartSpec): string {
  const { labels, datasets } = spec.data;
  const allVals = datasets.flatMap(ds => ds.values);
  const minVal = Math.min(0, ...allVals);
  const maxVal = Math.max(...allVals);
  const ticks = computeNiceTicks(minVal, maxVal, 5);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const yRange = yMax - yMin || 1;

  // Determine if labels should be rotated
  const maxLabelLen = Math.max(...labels.map(l => l.length));
  const chartW = W - PAD.left - PAD.right;
  const groupW = chartW / labels.length;
  const rotate = groupW < maxLabelLen * 7; // not enough room for horizontal labels
  const padBottom = rotate ? 90 : 40;
  const H = PAD.top + 160 + padBottom; // 160px chart area
  const chartH = H - PAD.top - padBottom;

  const barGap = 4;
  const dsCount = datasets.length;
  const barW = Math.max(4, (groupW - barGap * 2) / dsCount - 2);

  const yScale = (v: number) => PAD.top + chartH - ((v - yMin) / yRange) * chartH;
  const zeroY = yScale(0);

  let bars = '';
  for (let li = 0; li < labels.length; li++) {
    const gx = PAD.left + li * groupW + barGap;
    for (let di = 0; di < dsCount; di++) {
      const val = datasets[di].values[li] ?? 0;
      const bx = gx + di * (barW + 2);
      const by = yScale(val);
      const bh = Math.abs(zeroY - by);
      const top = val >= 0 ? by : zeroY;
      bars += `<rect x="${bx}" y="${top}" width="${barW}" height="${bh}" fill="${datasets[di].color}" rx="2"/>`;
    }
  }

  // Grid lines + Y axis labels
  const chartBottom = PAD.top + chartH;
  let grid = '';
  for (const t of ticks) {
    const y = yScale(t);
    grid += `<line class="chart-grid" x1="${PAD.left}" y1="${y}" x2="${W - PAD.right}" y2="${y}"/>`;
    grid += `<text x="${PAD.left - 12}" y="${y + 4}" text-anchor="end" font-size="10">${formatNum(t)}</text>`;
  }

  // Axis lines
  const axes = `<line x1="${PAD.left}" y1="${PAD.top}" x2="${PAD.left}" y2="${chartBottom}" stroke="#d1d5db" stroke-width="1"/>` +
    `<line x1="${PAD.left}" y1="${chartBottom}" x2="${W - PAD.right}" y2="${chartBottom}" stroke="#d1d5db" stroke-width="1"/>`;

  // X axis labels - text-anchor="end" with negative rotation makes text extend DOWN-LEFT from anchor
  const charLimit = rotate ? 14 : 10;

  let xLabels = '';
  const step = labels.length > 20 ? Math.ceil(labels.length / 20) : 1;
  for (let i = 0; i < labels.length; i += step) {
    const x = PAD.left + i * groupW + groupW / 2;
    const label = truncLabel(labels[i], charLimit);
    if (rotate) {
      const y = chartBottom + 14;
      xLabels += `<text x="${x}" y="${y}" text-anchor="end" font-size="9.5" transform="rotate(-45 ${x} ${y})">${escapeXml(label)}</text>`;
    } else {
      const y = chartBottom + 18;
      xLabels += `<text x="${x}" y="${y}" text-anchor="middle" font-size="10">${escapeXml(label)}</text>`;
    }
  }

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
    ${titleSvg(spec.title, W)}
    ${grid}
    ${axes}
    ${bars}
    ${xLabels}
  </svg>`;
}

// --- Line / Area Chart ---
function renderLine(spec: ChartSpec, filled: boolean): string {
  const { labels, datasets } = spec.data;
  const allVals = datasets.flatMap(ds => ds.values);
  const minVal = filled ? Math.min(0, ...allVals) : Math.min(...allVals);
  const maxVal = Math.max(...allVals);
  const ticks = computeNiceTicks(minVal, maxVal, 5);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const yRange = yMax - yMin || 1;

  const maxLabelLen = Math.max(...labels.map(l => l.length));
  const chartW = W - PAD.left - PAD.right;
  const xStep = labels.length > 1 ? chartW / (labels.length - 1) : chartW;
  const rotate = xStep < maxLabelLen * 7;
  const padBottom = rotate ? 90 : 40;
  const H = PAD.top + 160 + padBottom;
  const chartH = H - PAD.top - padBottom;

  const yScale = (v: number) => PAD.top + chartH - ((v - yMin) / yRange) * chartH;
  const xPos = (i: number) => PAD.left + i * xStep;

  let paths = '';
  for (const ds of datasets) {
    const points = ds.values.map((v, i) => `${xPos(i)},${yScale(v)}`);
    if (filled) {
      const baseY = yScale(yMin);
      const polygon = `${xPos(0)},${baseY} ${points.join(' ')} ${xPos(ds.values.length - 1)},${baseY}`;
      paths += `<polygon points="${polygon}" fill="${ds.color}" opacity="0.2"/>`;
    }
    paths += `<polyline points="${points.join(' ')}" fill="none" stroke="${ds.color}" stroke-width="2" stroke-linejoin="round"/>`;
    for (let i = 0; i < ds.values.length; i++) {
      paths += `<circle cx="${xPos(i)}" cy="${yScale(ds.values[i])}" r="3" fill="${ds.color}"/>`;
    }
  }

  // Grid + Y labels
  const chartBottom = PAD.top + chartH;
  let grid = '';
  for (const t of ticks) {
    const y = yScale(t);
    grid += `<line class="chart-grid" x1="${PAD.left}" y1="${y}" x2="${W - PAD.right}" y2="${y}"/>`;
    grid += `<text x="${PAD.left - 12}" y="${y + 4}" text-anchor="end" font-size="10">${formatNum(t)}</text>`;
  }

  // Axis lines
  const axes = `<line x1="${PAD.left}" y1="${PAD.top}" x2="${PAD.left}" y2="${chartBottom}" stroke="#d1d5db" stroke-width="1"/>` +
    `<line x1="${PAD.left}" y1="${chartBottom}" x2="${W - PAD.right}" y2="${chartBottom}" stroke="#d1d5db" stroke-width="1"/>`;

  // X labels
  const charLimit = rotate ? 14 : 10;

  let xLabels = '';
  const step = labels.length > 20 ? Math.ceil(labels.length / 20) : 1;
  for (let i = 0; i < labels.length; i += step) {
    const x = xPos(i);
    const label = truncLabel(labels[i], charLimit);
    if (rotate) {
      const y = chartBottom + 14;
      xLabels += `<text x="${x}" y="${y}" text-anchor="end" font-size="9.5" transform="rotate(-45 ${x} ${y})">${escapeXml(label)}</text>`;
    } else {
      const y = chartBottom + 18;
      xLabels += `<text x="${x}" y="${y}" text-anchor="middle" font-size="10">${escapeXml(label)}</text>`;
    }
  }

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
    ${titleSvg(spec.title, W)}
    ${grid}
    ${axes}
    ${paths}
    ${xLabels}
  </svg>`;
}

// --- Pie Chart ---
function renderPie(spec: ChartSpec): string {
  const ds = spec.data.datasets[0];
  if (!ds) return '';

  const total = ds.values.reduce((s, v) => s + Math.max(0, v), 0);
  if (total === 0) return `<svg viewBox="0 0 300 200" width="100%"><text x="150" y="100" text-anchor="middle">No data</text></svg>`;

  const pieW = 300;
  const cx = pieW / 2, cy = 110, r = 80;
  let startAngle = -Math.PI / 2;
  let slices = '';

  for (let i = 0; i < ds.values.length; i++) {
    const val = Math.max(0, ds.values[i]);
    const pct = val / total;
    const endAngle = startAngle + pct * 2 * Math.PI;
    const color = PALETTE[i % PALETTE.length];

    if (pct >= 0.999) {
      slices += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/>`;
    } else if (pct > 0) {
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);
      const large = pct > 0.5 ? 1 : 0;
      slices += `<path d="M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z" fill="${color}"/>`;
    }
    startAngle = endAngle;
  }

  const svgH = 200;
  return `<svg viewBox="0 0 ${pieW} ${svgH}" width="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
    ${titleSvg(spec.title, pieW)}
    ${slices}
  </svg>`;
}
